/* hs-eslint ignored failing-rules */

import { fetchCallsV1Settings } from 'calling-internal-common/calls-v1-settings/clients/fetchCallsV1Settings';
import FromNumber from 'calling-internal-common/calls-v1-settings/records/InitialLoadSettingsRecords/FromNumber';
import { PhoneNumberSources } from 'calling-internal-common/constants/PhoneNumberSources';
import { fetchCallingAppealState } from 'calling-lifecycle-internal/calling-appeals/clients/fetchCallingAppealState';
import { fetchCallsV1SettingsUsage } from 'calling-lifecycle-internal/calls-v1-settings-usage/client/fetchCallsV1SettingsUsage';
import { List } from 'immutable';
import { useEffect, useState } from 'react';
import { updateWidgetDetails } from 'worker-connection-library/hooks/useWidgetDetails';
import memoizeWithTimeout from 'calling-remote-common/utils/memoize';
import { getInstalledCallingSettings } from 'calling-integration-remote-app/utils/getInstalledCallingSettings';
import { hasCallingMinutesPerPortal } from 'calling-orchestration-shared-library/hub-info/utils/getCallingLimits';
let memoizedFetchCallsV1Settings = memoizeWithTimeout(() => {
  return fetchCallsV1Settings();
}, 86400);
let memoizedFetchCallingAppealState = memoizeWithTimeout(() => {
  return fetchCallingAppealState();
}, 86400);
let memoizedFetchCallsV1SettingsUsage = memoizeWithTimeout(() => {
  return fetchCallsV1SettingsUsage();
}, 86400);
export const useCallingSettings = props => {
  const {
    shouldFetchData
  } = props;
  const [error, setError] = useState(undefined);
  const [isCallingSettingsLoading, setIsCallingSettingsLoading] = useState(true);
  const [connectFromNumbers, setConnectFromNumbers] = useState([]);
  const [externalFromNumbers, setExternalFromNumbers] = useState([]);
  const [portalFromNumbers, setPortalFromNumbers] = useState([]);
  const [hasCallingAccess, setHasCallingAccess] = useState(null);
  const [hasExternalFromNumbers, setHasExternalFromNumbers] = useState(false);
  const [hasPortalFromNumbers, setHasPortalFromNumbers] = useState(false);
  const [hasConnectFromNumbers, setHasConnectFromNumbers] = useState(false);
  const [isHubSpotCallingEnabled, setIsHubSpotCallingEnabled] = useState(null);
  const [isRecordingEnabled, setIsRecordingEnabled] = useState(null);
  const [installedCallingSettings, setInstalledCallingSettings] = useState(List());
  useEffect(() => {
    if (!shouldFetchData) {
      return;
    }
    setIsCallingSettingsLoading(true);
    memoizedFetchCallsV1Settings().then(callsV1SettingsResponse => {
      const hubSpotCallingEnabled = callsV1SettingsResponse && callsV1SettingsResponse.callingAdminSettings && callsV1SettingsResponse.callingAdminSettings.result && callsV1SettingsResponse.callingAdminSettings.result.hubSpotCallingEnabled;
      // TODO Fix: https://hubspot.slack.com/archives/C016LJXTK35/p1668619337288809
      setIsHubSpotCallingEnabled(hubSpotCallingEnabled === null ? true : hubSpotCallingEnabled);
      const _connectFromNumbers = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.connectNumbers && callsV1SettingsResponse.initialLoadSettings.connectNumbers.map(connectNumber => {
        connectNumber.source = PhoneNumberSources.CONNECT_NUMBER;
        return new FromNumber(connectNumber);
      }) || [];
      setConnectFromNumbers(_connectFromNumbers);
      const _hasConnectFromNumbers = _connectFromNumbers ? _connectFromNumbers.length > 0 : false;
      setHasConnectFromNumbers(_hasConnectFromNumbers);
      const _externalFromNumbers = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.fromNumbers && callsV1SettingsResponse.initialLoadSettings.fromNumbers.map(externalFromNumber => {
        externalFromNumber.source = PhoneNumberSources.EXTERNAL_NUMBER;
        return new FromNumber(externalFromNumber);
      }) || [];
      setExternalFromNumbers(_externalFromNumbers);
      setHasExternalFromNumbers(_externalFromNumbers ? _externalFromNumbers.length > 0 : false);
      const _portalFromNumbers = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.portalFromNumbers && callsV1SettingsResponse.initialLoadSettings.portalFromNumbers.map(portalFromNumber => {
        portalFromNumber.source = PhoneNumberSources.HUBSPOT_NUMBER;
        return new FromNumber(portalFromNumber);
      }) || [];
      setPortalFromNumbers(_portalFromNumbers);
      setHasPortalFromNumbers(_portalFromNumbers ? _portalFromNumbers.length > 0 : false);
      const recordingEnabled = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.recordingEnabled;
      setIsRecordingEnabled(recordingEnabled);
      const _installedCallingSettings = getInstalledCallingSettings({
        callsV1SettingsResponse,
        hasTwilioConnect: _hasConnectFromNumbers
      });
      setInstalledCallingSettings(_installedCallingSettings);
    }).catch(() => {
      setError(new Error('Failed to fetch calls/v1/settings'));
    });
  }, [shouldFetchData]);
  useEffect(() => {
    if (!shouldFetchData || isHubSpotCallingEnabled === null) {
      return;
    }
    if (isHubSpotCallingEnabled === false) {
      setIsCallingSettingsLoading(false);
      return;
    }
    Promise.all([memoizedFetchCallingAppealState(), memoizedFetchCallsV1SettingsUsage()]).then(([appealStateResponse, callsV1SettingsUsageResponse]) => {
      const hasCallingMinutesPerPortalLimit = hasCallingMinutesPerPortal();
      const hasCallingAccessResp = !appealStateResponse.suspended && hasCallingMinutesPerPortalLimit;
      updateWidgetDetails({
        callsV1SettingsUsage: callsV1SettingsUsageResponse
      }).catch(() => {
        // do nothing, errors are handled in cross tab code
      });
      setHasCallingAccess(hasCallingAccessResp);
      setIsCallingSettingsLoading(false);
    }).catch(() => {
      setError(new Error('Failed to fetch /vetting/public/v2/account-verification/state/scope/calling-access or userInfo or /calling-orchestration/v1/settings/usage'));
      setIsCallingSettingsLoading(false);
    });
  }, [shouldFetchData, isHubSpotCallingEnabled, hasPortalFromNumbers]);
  return {
    error,
    connectFromNumbers,
    externalFromNumbers,
    portalFromNumbers,
    hasCallingAccess,
    hasExternalFromNumbers,
    hasConnectFromNumbers,
    hasPortalFromNumbers,
    isHubSpotCallingEnabled,
    isCallingSettingsLoading,
    isRecordingEnabled,
    installedCallingSettings
  };
};
export const clearMemoizedFetchForTesting = () => {
  memoizedFetchCallsV1Settings = fetchCallsV1Settings;
  memoizedFetchCallingAppealState = fetchCallingAppealState;
  memoizedFetchCallsV1SettingsUsage = fetchCallsV1SettingsUsage;
};